<div class="pay">

  <header class="pay__header">
    <div class="pay__header-left">
      @if (returnUrl()) {
        <button type="button" class="pay__back-btn" (click)="returnToCaller()" [attr.aria-label]="i18n.t('pay.back')">
          ← {{ i18n.t('pay.back') }}
        </button>
      }
      <img class="pay__logo" src="assets/logo.webp" alt="JJames SmartParking" />
    </div>

    <div class="pay__langs">
      <button type="button" class="pay__lang" [class.pay__lang--on]="i18n.lang() === 'de'"
        (click)="setLang('de')">DE</button>
      <button type="button" class="pay__lang" [class.pay__lang--on]="i18n.lang() === 'en'"
        (click)="setLang('en')">EN</button>
    </div>
  </header>

  <main class="pay__body">

    @switch (phase()) {

    <!-- ───────────────────────────────────────────────────────── loading -->
    @case ('loading') {
    <section class="card card--center">
      <div class="spinner" aria-hidden="true"></div>
      <p class="muted">{{ i18n.t('pay.loading') }}</p>
    </section>
    }

    <!-- ─────────────────────────────────────────── amount due + pay button -->
    @case ('quote') {
    <section class="card">
      <h1 class="title">{{ i18n.t('pay.title') }}</h1>

      @if (wasCancelled()) {
      <p class="notice notice--info">{{ i18n.t('pay.cancelled') }}</p>
      }
      @if (errorMessage()) {
      <p class="notice notice--warn">{{ errorMessage() }}</p>
      }

      <dl class="facts">
        @if (startedAtLabel()) {
        <div class="facts__row">
          <dt>{{ i18n.t('pay.entry') }}</dt>
          <dd>{{ startedAtLabel() }}</dd>
        </div>
        }
        @if (endedAtLabel()) {
        <div class="facts__row">
          <dt>{{ i18n.t('pay.exit') }}</dt>
          <dd>{{ endedAtLabel() }}</dd>
        </div>
        }
        @if (durationLabel()) {
        <div class="facts__row">
          <dt>{{ i18n.t('pay.duration') }}</dt>
          <dd>{{ durationLabel() }}</dd>
        </div>
        }
        @if (quote()?.zoneName) {
        <div class="facts__row">
          <dt>{{ i18n.t('pay.zone') }}</dt>
          <dd>{{ quote()?.zoneName }}</dd>
        </div>
        }
        @if (quote()?.spaceName) {
        <div class="facts__row">
          <dt>{{ i18n.t('pay.space') }}</dt>
          <dd>{{ quote()?.spaceName }}</dd>
        </div>
        }
        @if (hasPartialPayment()) {
        <div class="facts__row">
          <dt>{{ i18n.t('pay.gross') }}</dt>
          <dd>{{ grossLabel() }}</dd>
        </div>
        <div class="facts__row">
          <dt>{{ i18n.t('pay.alreadyPaid') }}</dt>
          <dd>−{{ paidLabel() }}</dd>
        </div>
        }
      </dl>

      <div class="amount">
        <span class="amount__label">{{ i18n.t('pay.amountDue') }}</span>
        <span class="amount__value">{{ amountLabel() }}</span>
      </div>

      @if (quote()?.obligationId) {
      <p class="muted muted--small">{{ i18n.t('pay.frozenNote') }}</p>
      }

      <button type="button" class="btn btn--primary" (click)="pay()">
        {{ i18n.t('pay.payNow') }}
      </button>

      @if (returnUrl()) {
        <button type="button" class="btn btn--ghost mt-2" (click)="returnToCaller()">
          ← {{ i18n.t('pay.back') }}
        </button>
      }

      <p class="muted muted--small">{{ i18n.t('pay.securedBy') }}</p>
    </section>
    }

    <!-- ────────────────────────────────────────────────── nothing to pay -->
    @case ('nothingDue') {
    <section class="card card--center">
      <div class="badge badge--ok" aria-hidden="true">✓</div>

      @switch (nothingDueReason()) {
      @case ('alreadyPaid') {
      <h1 class="title">{{ i18n.t('pay.done.alreadyPaid.title') }}</h1>
      <p class="muted">{{ i18n.t('pay.done.alreadyPaid.text') }}</p>
      }
      @case ('covered') {
      <h1 class="title">{{ i18n.t('pay.done.covered.title') }}</h1>
      <p class="muted">{{ i18n.t('pay.done.covered.text') }}</p>
      }
      @case ('freeStay') {
      <h1 class="title">{{ i18n.t('pay.done.free.title') }}</h1>
      <p class="muted">{{ i18n.t('pay.done.free.text') }}</p>
      }
      @case ('notPriceable') {
      <h1 class="title">{{ i18n.t('pay.done.notPriceable.title') }}</h1>
      <p class="muted">{{ i18n.t('pay.done.notPriceable.text') }}</p>
      }
      }

      @if (returnUrl()) {
        @if (redirectCountdown() !== null) {
          <p class="redirect-countdown">
            {{ i18n.t('pay.autoRedirect') }} {{ redirectCountdown() }} {{ i18n.t('pay.seconds') }}
          </p>
        }
        <button type="button" class="btn btn--primary mt-3" (click)="returnToCaller()">
          {{ i18n.t('pay.returnToApp') }} ➔
        </button>
      }
    </section>
    }

    <!-- ─────────────────────────────────────────── handing over to Stripe -->
    @case ('redirecting') {
    <section class="card card--center">
      <div class="spinner" aria-hidden="true"></div>
      <p class="muted">{{ i18n.t('pay.redirecting') }}</p>
    </section>
    }

    <!-- ───────────────────────────── back from Stripe, waiting for webhook -->
    @case ('confirming') {
    <section class="card card--center">
      @if (confirmTimedOut()) {
      <div class="badge badge--wait" aria-hidden="true">⏳</div>
      <h1 class="title">{{ i18n.t('pay.pending.title') }}</h1>
      <p class="muted">{{ i18n.t('pay.pending.text') }}</p>
      <button type="button" class="btn btn--ghost" (click)="recheck()">
        {{ i18n.t('pay.pending.recheck') }}
      </button>
      @if (returnUrl()) {
        <button type="button" class="btn btn--ghost mt-2" (click)="returnToCaller()">
          ← {{ i18n.t('pay.back') }}
        </button>
      }
      } @else {
      <div class="spinner" aria-hidden="true"></div>
      <h1 class="title">{{ i18n.t('pay.confirming.title') }}</h1>
      <p class="muted">{{ i18n.t('pay.confirming.text') }}</p>
      }
    </section>
    }

    <!-- ────────────────────────────────────────────────────── paid + receipt -->
    @case ('paid') {
    <section class="card card--center">
      <div class="badge badge--ok" aria-hidden="true">✓</div>
      <h1 class="title">{{ i18n.t('pay.paid.title') }}</h1>
      <p class="muted">{{ i18n.t('pay.paid.text') }}</p>

      @if (returnUrl()) {
        @if (redirectCountdown() !== null) {
          <p class="redirect-countdown">
            {{ i18n.t('pay.autoRedirect') }} {{ redirectCountdown() }} {{ i18n.t('pay.seconds') }}
          </p>
        }
        <button type="button" class="btn btn--primary mt-3" (click)="returnToCaller()">
          {{ i18n.t('pay.returnToApp') }} ➔
        </button>
      }

      <div class="receipt">
        <h2 class="receipt__title">
          {{ i18n.t(receipt()?.isFiscal === false ? 'pay.receipt.titlePayment' : 'pay.receipt.title') }}
        </h2>

        @if (receiptOpened()) {
        <p class="muted muted--small">{{ i18n.t('pay.receipt.opened') }}</p>
        } @else if (receiptQrData(); as receiptUrl) {
        <qrcode [qrdata]="receiptUrl" [width]="200" [margin]="1" [colorDark]="'#000000'" [colorLight]="'#FFFFFF'"
          [errorCorrectionLevel]="'M'" elementType="canvas" alt="Receipt QR code"></qrcode>
        <a class="receipt__link" [href]="receiptUrl" target="_blank" rel="noopener" (click)="onReceiptOpened()">
          {{ i18n.t(receipt()?.isFiscal === false ? 'pay.receipt.openPayment' : 'pay.receipt.open') }}
        </a>
        } @else if (receipt()?.status === 'Failed') {
        <p class="muted muted--small">{{ i18n.t('pay.receipt.failed') }}</p>
        } @else if (receiptUnavailable()) {
        <p class="muted muted--small">{{ i18n.t('pay.receipt.unavailable') }}</p>
        } @else {
        <div class="spinner spinner--sm" aria-hidden="true"></div>
        <p class="muted muted--small">{{ i18n.t('pay.receipt.generating') }}</p>
        }
      </div>
    </section>
    }

    <!-- ────────────────────────────────────────────────────────────── error -->
    @case ('error') {
    <section class="card card--center">
      <div class="badge badge--err" aria-hidden="true">!</div>
      <h1 class="title">{{ i18n.t('pay.error.title') }}</h1>
      <p class="muted">{{ errorMessage() }}</p>
      <button type="button" class="btn btn--ghost" (click)="loadQuote()">
        {{ i18n.t('pay.retry') }}
      </button>
      @if (returnUrl()) {
        <button type="button" class="btn btn--ghost mt-2" (click)="returnToCaller()">
          ← {{ i18n.t('pay.back') }}
        </button>
      }
    </section>
    }

    }

  </main>
</div>
